import api from "@/lib/api";
import { toast } from "sonner";
import { FileSignature, ArrowRight } from "lucide-react";
import { EditableSection, PlanStatuses } from "@/components/plan/DocSections";
import { PhasesSection } from "@/components/plan/PhasesSection";
import { QuotationEditor, MedicationTable } from "@/components/plan/QuoteMeds";
import { ImplantOutcomes } from "@/components/plan/ImplantOutcomes";

const ORDER = {
  implant: [["A", "Patient & consultation details", "patient_details"], ["B", "Diagnosis & clinical assessment", "diagnosis_assessment"], ["", "ICD-10-CM (Suggested)", "coding"],
    ["C", "", "phases"], ["", "Implant details", "implant_plan"], ["E", "", "meds"], ["F", "Final clinical summary", "final_summary"]],
  tmj: [["E", "Diagnosis & clinical decision support", "diagnosis_support"], ["", "Investigation recommendations", "investigations"], ["F", "", "phases"], ["G", "", "meds"],
    ["I", "Final clinical summary", "final_summary"], ["K", "Referral & red-flag status", "referral_status"], ["L", "ICD-10-CM & procedure coding", "coding"]],
  general: [["1", "Patient & consultation summary", "patient_details"], ["3", "Tooth-by-tooth examination", "tooth_exam"], ["4", "Diagnosis", "diagnosis_table"], ["6", "", "phases"],
    ["7", "Tooth-specific treatment", "tooth_treatments"], ["10", "Visit sequence", "visits"], ["", "", "meds"], ["8", "Coding (ICD-10-CM · local / CDT)", "coding"],
    ["9", "Prognosis & follow-up", "prognosis_follow_up"], ["", "Final summary", "final_summary"]],
};

function ConsentLink({ consent, onOpen }) {
  return (
    <button type="button" onClick={onOpen} data-testid="tp-consent-link" className="mb-6 flex w-full items-center gap-3 rounded-2xl border border-[#E2E8F0] bg-white p-4 text-left hover:border-[#00A85A]/50 dark:border-[#223139] dark:bg-[#0E1A21]">
      <FileSignature className="h-5 w-5 text-[#00A85A]" />
      <span className="font-bold text-[#101516] dark:text-white">Consent</span>
      <span className="text-[#687173] dark:text-[#8B9698]">{consent ? `${consent.status === "signed" ? "Signed" : consent.status === "sent" ? "Sent to patient" : "Draft"} · open Consent tab` : "Open Consent tab"}</span>
      <ArrowRight className="ml-auto h-4 w-4 text-[#00A85A]" />
    </button>
  );
}

export function SpecLayout({ plan, onChange, consent, onOpenConsent }) {
  const ct = plan.content || {};
  const slug = ORDER[plan.condition_slug] ? plan.condition_slug : "implant";
  const save = async (k, val) => {
    try { const { data } = await api.put(`/treatment-plans/${plan.id}`, { content: { ...ct, [k]: val }, section: k }); onChange(data); toast.success("Section saved"); }
    catch { toast.error("Couldn't save section"); }
  };
  return (
    <div data-testid="spec-layout">
      <div className="mb-6 space-y-4">
        {ORDER[slug].map(([l, t, k]) => {
          if (k === "phases") return <PhasesSection key={k} value={ct.phases} onSave={save} />;
          if (k === "meds") return plan.medications ? <MedicationTable key={k} plan={plan} onChange={onChange} /> : null;
          if (k === "implant_plan" && ct.implant_engine?.size?.length) return <ImplantOutcomes key={k} ct={ct} onSave={save} />;
          return <EditableSection key={k} letter="" title={t} k={k} value={ct[k]} onSave={save} />;
        })}
      </div>
      {plan.quotation && <QuotationEditor plan={plan} onChange={onChange} />}
      <ConsentLink consent={consent} onOpen={onOpenConsent} />
      <h3 className="mb-2 font-bold text-[#101516] dark:text-white">Approval status</h3>
      {plan.statuses && <PlanStatuses plan={plan} onChange={onChange} />}
    </div>
  );
}
